import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { toast } from "sonner";

export interface SavedMealItem {
  id: string;
  food_id?: string;
  user_food_id?: string;
  food_name: string;
  quantity: number;
  unit: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber?: number;
  sugar?: number;
  sodium?: number;
}

export interface SavedMeal {
  id: string;
  user_id: string;
  name: string;
  description?: string;
  meal_type: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  total_calories: number;
  total_protein: number;
  total_carbs: number;
  total_fat: number;
  total_fiber?: number;
  created_at: string;
  updated_at: string;
  items?: SavedMealItem[];
}

export const useSavedMeals = () => {
  const { user } = useAuth();
  const [savedMeals, setSavedMeals] = useState<SavedMeal[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchSavedMeals = async () => {
    if (!user) {
      setSavedMeals([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data: meals, error } = await supabase
        .from("saved_meals")
        .select(`
          *,
          items:saved_meal_items(*)
        `)
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setSavedMeals((meals || []) as SavedMeal[]);
    } catch (error) {
      console.error("Error fetching saved meals:", error);
      toast.error("Failed to load saved meals");
    } finally {
      setLoading(false);
    }
  };

  const createSavedMeal = async (
    name: string,
    description: string,
    mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack',
    items: Omit<SavedMealItem, "id">[]
  ) => {
    if (!user) {
      toast.error("Please log in to save meals");
      return null;
    }

    try {
      // Calculate totals
      const totals = items.reduce(
        (acc, item) => ({
          calories: acc.calories + item.calories,
          protein: acc.protein + item.protein,
          carbs: acc.carbs + item.carbs,
          fat: acc.fat + item.fat,
          fiber: acc.fiber + (item.fiber || 0),
          sugar: acc.sugar + (item.sugar || 0),
          sodium: acc.sodium + (item.sodium || 0),
        }),
        { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0, sodium: 0 }
      );

      // Create the saved meal
      const { data: meal, error: mealError } = await supabase
        .from("saved_meals")
        .insert({
          user_id: user.id,
          name,
          description,
          meal_type: mealType,
          total_calories: totals.calories,
          total_protein: totals.protein,
          total_carbs: totals.carbs,
          total_fat: totals.fat,
          total_fiber: totals.fiber,
          total_sugar: totals.sugar,
          total_sodium: totals.sodium,
        })
        .select()
        .single();

      if (mealError) throw mealError;

      // Add the items
      const itemsToInsert = items.map((item) => ({
        saved_meal_id: meal.id,
        food_id: item.food_id || null,
        user_food_id: item.user_food_id || null,
        food_name: item.food_name,
        quantity: item.quantity,
        unit: item.unit,
        calories: item.calories,
        sugar: item.sugar ?? 0,
        sodium: item.sodium ?? 0,
        protein: item.protein,
        carbs: item.carbs,
        fat: item.fat,
        fiber: item.fiber || 0,
      }));

      console.log('Inserting saved meal items:', itemsToInsert);

      const { data: insertedItems, error: itemsError } = await supabase
        .from("saved_meal_items")
        .insert(itemsToInsert)
        .select();

      if (itemsError) {
        console.error('Error inserting saved meal items:', itemsError);
        toast.error("Failed to save meal items. Please try again.");
        // Clean up the meal if items failed
        await supabase.from("saved_meals").delete().eq("id", meal.id);
        throw itemsError;
      }

      console.log('Inserted items:', insertedItems);

      toast.success("Meal saved successfully!");
      await fetchSavedMeals();
      return meal.id;
    } catch (error) {
      console.error("Error creating saved meal:", error);
      toast.error("Failed to save meal");
      return null;
    }
  };

  const updateSavedMeal = async (
    mealId: string,
    name: string,
    description: string,
    mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack',
    items: Omit<SavedMealItem, "id">[]
  ) => {
    if (!user) {
      toast.error("Please log in to update meals");
      return null;
    }

    try {
      // Calculate totals
      const totals = items.reduce(
        (acc, item) => ({
          calories: acc.calories + item.calories,
          protein: acc.protein + item.protein,
          carbs: acc.carbs + item.carbs,
          fat: acc.fat + item.fat,
          fiber: acc.fiber + (item.fiber || 0),
        }),
        { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 }
      );

      // Update the saved meal
      console.log('updateSavedMeal: Updating with meal_type:', mealType);
      const { error: mealError } = await supabase
        .from("saved_meals")
        .update({
          name,
          description,
          meal_type: mealType,
          total_calories: totals.calories,
          total_protein: totals.protein,
          total_carbs: totals.carbs,
          total_fat: totals.fat,
          total_fiber: totals.fiber,
        })
        .eq("id", mealId);

      console.log('updateSavedMeal: Update result:', { mealError });
      if (mealError) throw mealError;

      // Delete existing items
      const { error: deleteError } = await supabase
        .from("saved_meal_items")
        .delete()
        .eq("saved_meal_id", mealId);

      if (deleteError) throw deleteError;

      // Add the new items
      const itemsToInsert = items.map((item) => ({
        saved_meal_id: mealId,
        food_id: item.food_id || null,
        user_food_id: item.user_food_id || null,
        food_name: item.food_name,
        quantity: item.quantity,
        unit: item.unit,
        calories: item.calories,
        protein: item.protein,
        carbs: item.carbs,
        fat: item.fat,
        fiber: item.fiber || 0,
      }));

      const { error: itemsError } = await supabase
        .from("saved_meal_items")
        .insert(itemsToInsert);

      if (itemsError) throw itemsError;

      toast.success("Meal updated successfully!");
      await fetchSavedMeals();
      return mealId;
    } catch (error) {
      console.error("Error updating saved meal:", error);
      toast.error("Failed to update meal");
      return null;
    }
  };

  const deleteSavedMeal = async (mealId: string) => {
    try {
      const { error } = await supabase
        .from("saved_meals")
        .delete()
        .eq("id", mealId);

      if (error) throw error;

      toast.success("Meal deleted");
      await fetchSavedMeals();
    } catch (error) {
      console.error("Error deleting saved meal:", error);
      toast.error("Failed to delete meal");
    }
  };

  useEffect(() => {
    fetchSavedMeals();
  }, [user]);

  return {
    savedMeals,
    loading,
    createSavedMeal,
    updateSavedMeal,
    deleteSavedMeal,
    refreshSavedMeals: fetchSavedMeals,
  };
};